/**
 * ui_action tag parser.
 *
 * Extracts <ui_action> tags from AI response text, similar to how
 * <tour_suggestion> tags are parsed server-side. The cleaned text
 * (with tags removed) and parsed actions are returned.
 *
 * Supported action types:
 * - navigate:        <ui_action type="navigate" tab="sales" />
 * - highlight:       <ui_action type="highlight" region="create-actions" label="Click here" />
 * - scroll_to:       <ui_action type="scroll_to" region="line-items" />
 * - sandbox:         <ui_action type="sandbox" label="Take me to sandbox" />
 * - open_assistant:  <ui_action type="open_assistant" label="Open AI Assistant" />
 * - clear:           <ui_action type="clear" />
 *
 * The parser is tolerant of common LLM formatting mistakes:
 * - Missing self-closing slash: <ui_action type="clear">  (handled)
 * - Missing closing quote on an attribute, causing the next attribute
 *   to be swallowed: type="highlight region="..."  (handled — type is
 *   matched against known values, region/label/tab are extracted
 *   independently)
 * - Extra whitespace or newlines inside the tag.
 */

export interface ParsedUIAction {
  type: 'navigate' | 'highlight' | 'scroll_to' | 'sandbox' | 'open_assistant' | 'clear';
  tab?: string;
  region?: string;
  label?: string;
}

const VALID_TYPES = ['navigate', 'highlight', 'scroll_to', 'sandbox', 'open_assistant', 'clear'] as const;

/**
 * Parse <ui_action> tags from AI response text.
 * Returns the cleaned text (tags removed) and the parsed actions.
 */
export function extractUIActions(responseText: string): {
  cleanedText: string;
  actions: ParsedUIAction[];
} {
  const actions: ParsedUIAction[] = [];

  // Match <ui_action ... /> (self-closing) OR <ui_action ...> (non-self-closing).
  // The attribute body is everything up to the first /> or > that closes the tag.
  // We use a non-greedy match and accept both closing forms.
  const tagRegex = /<ui_action\s+([^]*?)(?:\/>|>)/gi;

  const cleanedText = responseText.replace(tagRegex, (_match, attrs: string) => {
    const action = parseAttrs(attrs);
    if (action) {
      actions.push(action);
    }
    return '';
  });

  // Normalize whitespace where tags were removed
  const normalized = cleanedText
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { cleanedText: normalized, actions };
}

/**
 * Parse attributes from a ui_action tag's inner text.
 *
 * Handles malformed attributes where the LLM forgets to close a quote,
 * e.g. `type="highlight region="module-grid"` — the `type` regex below
 * matches only known action type values, so it correctly extracts
 * `highlight` instead of `highlight region=`.
 */
function parseAttrs(attrs: string): ParsedUIAction | null {
  // Match type against the known list of valid types. This is resilient
  // to missing closing quotes — e.g. `type="highlight region="foo"` will
  // still match `highlight` because the alternation stops at the first
  // valid keyword.
  const typeMatch = attrs.match(new RegExp(`type=["']?(?:${VALID_TYPES.join('|')})["']?`, 'i'));
  const typeStr = typeMatch
    ? typeMatch[0].match(new RegExp(VALID_TYPES.join('|'), 'i'))?.[0].toLowerCase()
    : undefined;

  if (!typeStr) {
    return null;
  }

  const type = typeStr as ParsedUIAction['type'];

  // For other attributes, use standard quoted-value matching. These are
  // less prone to the missing-quote issue since they typically appear
  // after the type attribute.
  const tabMatch = attrs.match(/tab=["']([^"']+)["']/i);
  const regionMatch = attrs.match(/region=["']([^"']+)["']/i);
  const labelMatch = attrs.match(/label=["']([^"']+)["']/i);

  return {
    type,
    tab: tabMatch?.[1],
    region: regionMatch?.[1],
    label: labelMatch?.[1],
  };
}
